import { createTheme } from '@mui/material/styles';

/**
 * MUI v9 theme — mirrors the v4 theme (config/theme.js) with v9 API.
 *
 * During the incremental migration every component that switches from
 * @material-ui/core to @mui/material will automatically pick up this
 * theme via the v9 ThemeProvider added in src/index.js.
 *
 * Custom `colors` are stored under `colors` for backwards-compat with
 * existing component code (e.g. theme.colors.lightBlue).
 *
 * The theme is built per colour mode by `createAppTheme`, which the
 * provider calls again when the user switches theme. The palette must
 * hold REAL colours: MUI and libraries that read its palette run
 * colour maths on those values (notistack calls `emphasize` on
 * `background.default`), and a CSS variable throws there, which takes
 * the whole app down. Keep these literals in step with
 * `shared/ui/tokens/tokens.css`.
 */

const MODE_PALETTES = {
  light: {
    mode: 'light',
    text: { primary: '#111827', secondary: '#4b5563', disabled: '#d1d5db' },
    background: { default: '#f5f7fa', paper: '#ffffff' },
    divider: '#e5e7eb',
  },
  dark: {
    mode: 'dark',
    text: { primary: '#e5e7eb', secondary: '#9ca3af', disabled: '#4b5563' },
    background: { default: '#0d1117', paper: '#161b25' },
    divider: '#252b3a',
  },
};

export const createAppTheme = (mode = 'light') =>
  createTheme({
    palette: {
      primary: {
        main: '#00A8FF',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#8E8E8E',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#DC125B',
        light: '#d79f9a',
      },
      error: {
        main: '#dc1212',
        light: '#d79f9a',
      },
      success: {
        main: '#41C377',
        light: '#93c4a8',
      },
      info: {
        main: '#928BB7',
        light: '#FAF8FF',
        contrastText: '#484554',
      },
      // Surfaces and text follow the user's theme, so every MUI
      // component (Drawer, Dialog, Popover) and whatever inherits its
      // colour reads correctly in both.
      ...MODE_PALETTES[mode === 'dark' ? 'dark' : 'light'],
    },
    typography: {
      // MUI v4 used 14px as the default body font size; v9 defaults to 16px.
      // Restore v4-compatible sizing to prevent global font-size increase.
      fontSize: 14,
      body1: {
        fontSize: '0.875rem', // 14px — matches v4 body2 which was the default
      },
      body2: {
        fontSize: '0.875rem',
      },
      button: {
        textTransform: 'none',
      },
    },
    // Explicit breakpoints matching v4 defaults to preserve responsive behavior.
    // MUI v9 defaults differ (md: 900, lg: 1200, xl: 1536), which shifts layout.
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    shape: {
      borderRadius: 4,
    },
    colors: {
      navBg: '#3E3E44',
      navBgHover: '#4A494F',
      gray1: '#D9D9D9',
      gray2: '#E4E4E4',
      gray3: '#e7e7e7',
      gray4: '#818FA0',
      gray5: '#BDC5D0',
      grey: '#8E8E8E',
      grey2: '#000000',
      grey3: '#EBEBEB',
      grey4: '#DEDEDE',
      blue: '#2E48D3',
      blue2: '#031F42',
      blue3: '#001899',
      blue4: '#1b71e6',
      blue5: '#1C35BC',
      green: '#41C377',
      darkGreen: '#0d9146',
      orange: '#EA7502',
      lightOrange: '#f3b16c',
      lightBlue: '#00A8FF',
      lightBlue2: '#6C7FDF',
      lightBlue3: '#4DC2FF',
      errorPink: '#e3d7d5',
      errorPinkLight: '#f2eceb',
      testing: '#4aa7f8',
      development: '#8a0101',
      darkError: '#dc1212',

      badge_green: '#29ac60',
      badge_dark_green: '#077336',
      badge_light_blue: '#0085ca',
      badge_light_grey: '#5e5e5e',
      badge_purple: '#5265c3',
      badge_orange: '#c86300',
      badge_gray: '#5e5e5e',
      badge_pink: '#c94fd7',

      // Text colors
      textPrimary: '#333333',
      textSecondary: '#666666',
      textDisabled: '#999999',

      // Background colors
      backgroundLight: '#f8f9fa',
      backgroundHover: '#f0f2ff',

      // Border colors
      border: '#e0e0e0',
      borderLight: '#eeeeee',
    },
    components: {
      MuiListItemText: {
        defaultProps: {
          // MUI v4 ListItemText used body2 (14px) for primary text;
          // v9 changed default to body1 (16px). Restore v4 behavior.
          primaryTypographyProps: { variant: 'body2' },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            // MUI v4 MenuItem used body1 at 14px; v9 uses 16px.
            fontSize: '0.875rem',
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          // MUI v4 margin="dense" controlled input height; v9 uses size prop.
          // Set size="small" globally to restore v4-compatible compact height.
          size: 'small',
        },
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
          },
          // v4 dense (size="small"): 10.5px 14px → ~40px total height.
          // Default size="small" is set globally via MuiTextField defaultProps.
          inputSizeSmall: {
            padding: '10.5px 14px',
          },
          // v4 default (size="medium"): 18.5px 14px → 56px total height.
          // Only applies when size="medium" is explicitly set.
          inputSizeMedium: {
            padding: '18.5px 14px',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            // Match MUI v9 outlined input line-height so the label box height
            // is predictable (otherwise label appears top-aligned in the input).
            lineHeight: '1.4375em',
          },
          // Label position for size="small" with v4-compatible 40px input height
          // (input padding 10.5px 14px). Default v9 is translate(14px, 9px) for
          // 32px input — we shift down 2px to compensate for our larger padding.
          sizeSmall: {
            transform: 'translate(14px, 11px) scale(1)',
            // Shrunk position must sit on top of the notched outline border.
            // v9 default is -9px which aligns label center with the border line.
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          marginNormal: {
            marginTop: 8,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            // MUI v4 IconButton default padding was 12px; v9 uses 8px.
            // Restore v4-compatible sizing.
            padding: 12,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          contained: {
            color: 'white !important',
          },
        },
      },
    },
  });

/**
 * Light instance for the few modules that import the theme directly
 * rather than through the provider (chart export, Mantine's provider
 * in the shared Table).
 */
const themeV9 = createAppTheme('light');

export default themeV9;
